import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  Speed as SpeedIcon,
  Settings as SettingsIcon,
  Backup as BackupIcon,
  Announcement as AnnouncementIcon,
  Support as SupportIcon,
  Feedback as FeedbackIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

import Dashboard from './Dashboard';
import ClientManagement from './ClientManagement';
import BillingManagement from './BillingManagement';
import SystemHealth from './SystemHealth';
import BackupManagement from './BackupManagement';
import AnnouncementManagement from './AnnouncementManagement';
import SupportManagement from './SupportManagement';
import FeedbackManagement from './FeedbackManagement';
import TodoManagement from './TodoManagement';
import { OwnerProvider } from '../../contexts/OwnerContext';

const drawerWidth = 240;

const OwnerDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState('dashboard');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, value: 'dashboard' },
    { text: 'Client Management', icon: <PeopleIcon />, value: 'clients' },
    { text: 'Billing', icon: <ReceiptIcon />, value: 'billing' },
    { text: 'System Health', icon: <SpeedIcon />, value: 'health' },
    { text: 'Backup Management', icon: <BackupIcon />, value: 'backup' },
    { text: 'Public Announcements', icon: <AnnouncementIcon />, value: 'announcements' },
    { text: 'Support & Feedback', icon: <SupportIcon />, value: 'support' },
    { text: 'Todo Management', icon: <AssignmentIcon />, value: 'todos' },
    { text: 'Settings', icon: <SettingsIcon />, value: 'settings' }
  ];

  const renderContent = () => {
    switch (selectedPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'clients':
        return <ClientManagement />;
      case 'billing':
        return <BillingManagement />;
      case 'health':
        return <SystemHealth />;
      case 'backup':
        return <BackupManagement />;
      case 'announcements':
        return <AnnouncementManagement />;
      case 'support':
        return <SupportManagement />;
      case 'todos':
        return <TodoManagement />;
      default:
        return <Dashboard />;
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Owner Portal
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={selectedPage === item.value}
              onClick={() => {
                setSelectedPage(item.value);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <OwnerProvider>
      <Box sx={{ display: 'flex' }}>
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` }
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {menuItems.find((item) => item.value === selectedPage)?.text}
            </Typography>
            {/* Dashboard Return Icon */}
            <Tooltip title="Return to Main Dashboard">
              <IconButton
                size="large"
                edge="end"
                aria-label="return to dashboard"
                onClick={() => window.location.href = '/dashboard'}
                color="inherit"
                sx={{ mr: 2 }}
              >
                <DashboardIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true // Better open performance on mobile
            }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth
              }
            }}
          >
            {drawer}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            mt: '64px'
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </OwnerProvider>
  );
};

export default OwnerDashboard; 